/**
 * Admin plan editor modal — quick-add a plan on a creator's behalf (lands
 * Approved; no notification email) or edit any plan's fields. Admin edits
 * never change status — approve/decline lives in EntryDetail.
 *
 * Four titled sections, in the order the fields depend on each other:
 * Who (page, then its channels), What (moment, title, format, plan status),
 * When (precision first — it decides the date input — then the date, an
 * optional end date, recurrence) and Notes. Validation walks the same order,
 * so the first complaint is about the first field that needs attention.
 *
 * Moments: an entry references a tentpole OR a free-form custom moment
 * ('✦ Custom moment…' select option). The selected report page's channel
 * roster is always visible, with All current channels as a snapshot shortcut.
 * plannedEndDate turns a plan into a range; on edit, '' clears (backend
 * ''-clears-field convention).
 *
 * Edits send only the fields the form changed, with the updatedAt the editor
 * opened with: a plan changed since is refused (409 stale), not overwritten,
 * and the modal stays open with what was typed.
 *
 * The entry id is a client-minted UUID (idempotent create: a retry after a
 * network blip re-sends the same id and the server answers 200, not a dupe).
 * A retry after the form changed is applied to the saved plan as an edit (409
 * already_saved); an id another owner holds is swapped for a fresh one, once
 * (409 id_conflict).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner } from '../../ui';
import {
  createCalendarEntry,
  listCalendarPages,
  updateCalendarEntry,
} from '../../../services/tentpoleCalendarApi';
import FilterChipGroup from '../FilterChipGroup';
import { fieldStyle, labelStyle } from '../MediaPlans/formStyles';
import {
  advertiserSummaryOf,
  CALENDAR_FIELD_LABELS,
  fmtDate,
  isUpcomingTentpole,
  planStatusOf,
  recurrenceOf,
  targetScopeLabel,
  tentpoleLabel,
} from './shared';
import {
  CALENDAR_PLAN_STATUSES,
  CALENDAR_RECURRENCES,
  CalendarContentType,
  CalendarDatePrecision,
  CalendarEntry,
  CalendarPageOption,
  CalendarPlanStatus,
  CalendarRecurrence,
  CONTENT_TYPE_LABELS,
  PLAN_STATUS_LABELS,
  PRECISION_LABELS,
  RECURRENCE_LABELS,
  Tentpole,
} from './types';
import { AdminQuickAddDraft, buildAdminEntryPatch, buildAdminQuickAddPayload } from './entryEditorPayload';

interface EntryEditorProps {
  entry: CalendarEntry | null; // null = create
  tentpoles: Tentpole[];
  presetTentpoleSlug: string | null;
  onClose: () => void;
  onSaved: () => void;
  /** The plan changed on the server since the editor opened (409 stale). The
   *  editor stays open with what was typed; the caller refreshes the calendar. */
  onStale?: () => void;
  notify: (kind: 'success' | 'error', title: string, message?: string) => void;
}

const hintStyle: React.CSSProperties = { fontSize: 12, lineHeight: 1.45, color: 'var(--fg-3)', marginTop: 6 };

const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

/** Outranks the uppercase field labels, so the four sections read at a glance. */
const sectionHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--fg-1)',
};

const sectionRuleStyle: React.CSSProperties = {
  paddingTop: 22,
  borderTop: '1px solid var(--border-subtle)',
};

const channelPickerStyle: React.CSSProperties = {
  maxHeight: 220,
  overflowY: 'auto',
  marginTop: 6,
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--bg-surface-1)',
};

const channelOptionStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '18px minmax(0, 1fr)',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-subtle)',
  cursor: 'pointer',
  fontSize: 13,
};

/** A text link, so it opts out of the global button hover lift. */
const linkButtonStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  padding: 0,
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  transform: 'none',
  color: 'var(--accent)',
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  cursor: 'pointer',
};

const CONTENT_TYPE_OPTIONS = (Object.keys(CONTENT_TYPE_LABELS) as CalendarContentType[])
  .map((value) => ({ value, label: CONTENT_TYPE_LABELS[value] }));

const PRECISION_OPTIONS = (Object.keys(PRECISION_LABELS) as CalendarDatePrecision[])
  .map((value) => ({ value, label: PRECISION_LABELS[value] }));

const initialTargetIds = (entry: CalendarEntry | null): string[] => {
  if (!entry) return [];
  if (entry.targetChannelIds && entry.targetChannelIds.length > 0) return entry.targetChannelIds;
  if (entry.targetChannels && entry.targetChannels.length > 0) {
    return entry.targetChannels.map((channel) => channel.channelId);
  }
  return entry.channelId ? [entry.channelId] : [];
};

const Section: React.FC<{ title: string; first?: boolean; children: React.ReactNode }> = ({ title, first, children }) => (
  <section style={first ? sectionStyle : { ...sectionStyle, ...sectionRuleStyle }}>
    <h3 style={sectionHeadingStyle}>{title}</h3>
    {children}
  </section>
);

const EntryEditor: React.FC<EntryEditorProps> = ({
  entry,
  tentpoles,
  presetTentpoleSlug,
  onClose,
  onSaved,
  onStale,
  notify,
}) => {
  // Minted once per open create-modal; the same id is re-sent on retry.
  const [entryId, setEntryId] = useState(() => entry?.id ?? crypto.randomUUID());
  const [creatorSlug, setCreatorSlug] = useState(entry?.creatorSlug ?? '');
  const [tentpoleSlug, setTentpoleSlug] = useState(entry?.tentpoleSlug ?? presetTentpoleSlug ?? '');
  const [momentMode, setMomentMode] = useState<'tentpole' | 'custom'>(entry?.customLabel ? 'custom' : 'tentpole');
  const [customLabel, setCustomLabel] = useState(entry?.customLabel ?? '');
  const [scopeMode, setScopeMode] = useState<'all' | 'selected'>(
    entry?.scopeMode === 'selected' || (!!entry?.channelId && !entry?.scopeMode) ? 'selected' : 'all',
  );
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>(() => initialTargetIds(entry));
  // A legacy row held for a manager decision is approvable only after its
  // scope is saved explicitly (the detail banner sends managers here), so its
  // current scope counts as a change from the start.
  const [scopeDirty, setScopeDirty] = useState(
    !entry || !!(entry.requiresManagerReview || entry.migrationState),
  );
  const [channelSearch, setChannelSearch] = useState('');
  const [title, setTitle] = useState(entry?.title ?? '');
  const [contentType, setContentType] = useState<CalendarContentType>(entry?.contentType ?? 'video');
  const [plannedDate, setPlannedDate] = useState(entry?.plannedDate ?? '');
  const [plannedEndDate, setPlannedEndDate] = useState(entry?.plannedEndDate ?? '');
  // The end date stays tucked away until it's wanted (or already set).
  const [showEndDate, setShowEndDate] = useState(!!entry?.plannedEndDate);
  const [datePrecision, setDatePrecision] = useState<CalendarDatePrecision>(entry?.datePrecision ?? 'exact');
  const [recurrence, setRecurrence] = useState<CalendarRecurrence>(() => entry ? recurrenceOf(entry) : 'one_time');
  const [planStatus, setPlanStatus] = useState<CalendarPlanStatus>(() => planStatusOf(entry));
  const [advertiserSummary, setAdvertiserSummary] = useState(entry ? advertiserSummaryOf(entry) : '');
  const [internalNote, setInternalNote] = useState(entry?.internalNote ?? '');
  const [saving, setSaving] = useState(false);

  // Creator pages — the picker on create, the channel roster in both modes.
  const [pages, setPages] = useState<CalendarPageOption[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pageFilter, setPageFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPagesLoading(true);
    listCalendarPages()
      .then((p) => !cancelled && setPages(p))
      .catch((err) => !cancelled && notify('error', 'Failed to load creator pages', err?.message))
      .finally(() => !cancelled && setPagesLoading(false));
    return () => { cancelled = true; };
  }, [entry, notify]);

  const filteredPages = useMemo(() => {
    const q = pageFilter.trim().toLowerCase();
    return pages
      .filter((p) => !p.revoked)
      .filter((p) => !q || p.clientLabel.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
      .sort((a, b) => a.clientLabel.localeCompare(b.clientLabel));
  }, [pages, pageFilter]);

  // Page labels shown more than once get their slug, so the options stay distinguishable.
  const sharedPageLabels = useMemo(() => {
    const seen = new Set<string>();
    const shared = new Set<string>();
    pages.filter((p) => !p.revoked).forEach((p) => {
      if (seen.has(p.clientLabel)) shared.add(p.clientLabel);
      seen.add(p.clientLabel);
    });
    return shared;
  }, [pages]);

  // Channel roster of the selected page (creator locked on edit).
  const selectedPage = pages.find((p) => p.slug === (entry ? entry.creatorSlug : creatorSlug));
  const channels = selectedPage?.channels ?? [];
  const allCurrentChannelIds = channels.map((channel) => channel.channelId);
  const channelLabelOf = (channel: typeof channels[number]): string =>
    channel.channelLabel || channel.channelName || channel.channelId;
  const snapshotLabels = new Map(
    (entry?.targetChannels ?? []).map((channel) => [channel.channelId, channel.channelLabel]),
  );
  if (entry?.channelId && !snapshotLabels.has(entry.channelId)) {
    snapshotLabels.set(entry.channelId, entry.channelLabel || entry.channelId);
  }
  const staleSelectedChannels = selectedChannelIds
    .filter((id) => !channels.some((channel) => channel.channelId === id))
    .map((id) => ({ channelId: id, channelName: `${snapshotLabels.get(id) || id} (no longer on page)` }));
  const visibleChannels = [...staleSelectedChannels, ...channels].filter((channel) => {
    const q = channelSearch.trim().toLowerCase();
    return !q || channelLabelOf(channel).toLowerCase().includes(q) || channel.channelId.toLowerCase().includes(q);
  });
  const rosterSize = channels.length || entry?.targetChannels?.length || 0;

  const toggleAllCurrentChannels = (checked: boolean) => {
    setScopeMode(checked ? 'all' : 'selected');
    setScopeDirty(true);
    if (!checked && selectedChannelIds.length === 0) {
      // Start explicit mode with the whole roster checked so the user can
      // simply uncheck the channels the plan does not apply to.
      setSelectedChannelIds(allCurrentChannelIds);
    }
  };

  const toggleChannel = (channelId: string) => {
    setSelectedChannelIds((current) => {
      const allowed = new Set(allCurrentChannelIds);
      const basis = scopeMode === 'all'
        ? allCurrentChannelIds
        : current.filter((id) => allowed.has(id));
      // A saved target can remain visible after it is removed from the page.
      // Test membership before filtering so clicking that stale checked row
      // actually removes it instead of accidentally re-adding it forever.
      const wasSelected = current.includes(channelId) || (scopeMode === 'all' && allowed.has(channelId));
      return wasSelected
        ? basis.filter((id) => id !== channelId)
        : allowed.has(channelId) ? [...basis, channelId] : basis;
    });
    setScopeMode('selected');
    setScopeDirty(true);
  };

  // Upcoming tentpoles first (soonest window first), then past ones newest-first.
  const { upcomingTentpoles, pastTentpoles } = useMemo(() => ({
    upcomingTentpoles: tentpoles.filter(isUpcomingTentpole).sort((a, b) => a.startDate.localeCompare(b.startDate)),
    pastTentpoles: tentpoles.filter((t) => !isUpcomingTentpole(t)).sort((a, b) => b.startDate.localeCompare(a.startDate)),
  }), [tentpoles]);
  const tentpoleOption = (t: Tentpole) => (
    <option key={t.slug} value={t.slug}>
      {tentpoleLabel(t)} · {fmtDate(t.startDate)}{t.active ? '' : ' (inactive)'}
    </option>
  );

  const setPrecision = (next: CalendarDatePrecision) => {
    setDatePrecision(next);
    // Month precision stores the 1st — snap any picked day to it.
    if (next === 'month' && plannedDate) setPlannedDate(`${plannedDate.slice(0, 7)}-01`);
  };

  // Create, resolving a replay of this form's id (see the header comment).
  // Returns the creator whose calendar still holds an earlier save of this
  // form, when that save landed on a different page than the one now picked.
  const createEntry = async (draft: AdminQuickAddDraft): Promise<{ earlierCopyOn?: string }> => {
    try {
      await createCalendarEntry(buildAdminQuickAddPayload(draft));
      return {};
    } catch (err: any) {
      const saved: CalendarEntry | undefined = err?.code === 'already_saved' ? err.body?.entry : undefined;
      if (saved && saved.creatorSlug === draft.creatorSlug) {
        await updateCalendarEntry(saved.id, buildAdminEntryPatch(draft, saved, true), saved.updatedAt);
        return {};
      }
      // A plan can't move to another page, so a replay saved for a different
      // page stays as it is and this form becomes a new plan, like an id clash.
      if (!saved && err?.code !== 'id_conflict') throw err;
      const freshId = crypto.randomUUID();
      setEntryId(freshId);
      await createCalendarEntry(buildAdminQuickAddPayload({ ...draft, entryId: freshId }));
      return saved ? { earlierCopyOn: saved.creatorLabel || saved.creatorSlug } : {};
    }
  };

  const save = async () => {
    const selectedCurrentChannelIds = selectedChannelIds.filter((id) => allCurrentChannelIds.includes(id));
    // In the order the form shows the fields: Who, What, When.
    if (!entry && !creatorSlug) return notify('error', 'Pick a creator');
    // Scope is sent only when it changed, so only then must it name a channel:
    // a plan whose channels have all left the page can still be renamed.
    if (scopeDirty && scopeMode === 'selected' && selectedCurrentChannelIds.length === 0) {
      return notify('error', 'Choose at least one channel, or use All current channels');
    }
    if (momentMode === 'tentpole' && !tentpoleSlug) return notify('error', 'Pick a tentpole');
    if (momentMode === 'custom' && !customLabel.trim()) return notify('error', 'Name the custom moment');
    if (momentMode === 'custom' && customLabel.trim().length > 80) {
      return notify('error', 'Keep the moment name under 80 characters');
    }
    if (!title.trim()) return notify('error', 'Title is required');
    if (!plannedDate) return notify('error', 'Planned date is required');
    if (plannedEndDate && plannedEndDate < plannedDate) {
      return notify('error', 'End date must be on or after the planned date');
    }
    const draft: AdminQuickAddDraft = {
      entryId,
      creatorSlug,
      momentMode,
      tentpoleSlug,
      customLabel,
      scopeMode,
      selectedChannelIds: selectedCurrentChannelIds,
      title,
      contentType,
      plannedDate,
      plannedEndDate,
      datePrecision,
      recurrence,
      planStatus,
      advertiserSummary,
      internalNote,
    };
    setSaving(true);
    try {
      if (entry) {
        const patch = buildAdminEntryPatch(draft, entry, scopeDirty);
        if (Object.keys(patch).length === 0) {
          // Nothing changed: there is no PATCH to send.
          notify('success', 'No changes to save');
          onClose();
          return;
        }
        await updateCalendarEntry(entry.id, patch, entry.updatedAt);
        notify('success', 'Plan updated', title.trim());
      } else {
        const { earlierCopyOn } = await createEntry(draft);
        notify(
          'success',
          'Plan added',
          earlierCopyOn
            ? `Lands on the calendar as Approved. An earlier save of this form is on ${earlierCopyOn}'s calendar too; delete it there if it isn't wanted.`
            : 'Lands on the calendar as Approved.',
        );
      }
      onSaved();
      onClose();
    } catch (err: any) {
      notify('error', 'Save failed', err?.message);
      // The modal stays open, so nothing typed is lost.
      if (err?.code === 'stale') onStale?.();
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = selectedChannelIds.filter((id) => allCurrentChannelIds.includes(id)).length;

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Tentpole Calendar"
      title={entry ? 'Edit plan' : 'Add plan'}
      width={640}
      closeOnEsc={false}
      footer={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>
            {!entry && <>Lands as <strong>Approved</strong> · no email to the creator</>}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : entry ? 'Save changes' : 'Add plan'}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <Section title="Who" first>
          <div>
            <label style={labelStyle}>Creator page</label>
            {entry ? (
              <>
                <div style={{ fontSize: 14, color: 'var(--fg-1)' }} title={entry.creatorSlug}>{entry.creatorLabel}</div>
                <div style={hintStyle}>A plan stays on the page it was made for.</div>
              </>
            ) : pagesLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <Spinner size={16} /> <span style={{ fontSize: 13, color: 'var(--fg-3)' }}>Loading creator pages…</span>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8 }}>
                  <input
                    style={fieldStyle}
                    value={pageFilter}
                    onChange={(e) => setPageFilter(e.target.value)}
                    placeholder="Filter by name…"
                    aria-label="Filter creator pages"
                  />
                  <select
                    style={{ ...fieldStyle, cursor: 'pointer' }}
                    value={creatorSlug}
                    aria-label="Creator report page"
                    onChange={(e) => {
                      const nextSlug = e.target.value;
                      const nextPage = pages.find((page) => page.slug === nextSlug);
                      setCreatorSlug(nextSlug);
                      setScopeMode('all');
                      setSelectedChannelIds((nextPage?.channels ?? []).map((channel) => channel.channelId));
                      setScopeDirty(true);
                      setChannelSearch('');
                    }}
                  >
                    <option value="">Select a creator page…</option>
                    {filteredPages.map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.clientLabel}
                        {sharedPageLabels.has(p.clientLabel) ? ` (${p.slug})` : ''}
                        {p.pageType === 'agency' ? ' · agency' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={hintStyle}>
                  {pageFilter.trim()
                    ? `${filteredPages.length} ${filteredPages.length === 1 ? 'match' : 'matches'}. `
                    : ''}
                  Plans attach to a creator report page, so creators without one can't be added yet.
                </div>
              </>
            )}
          </div>

          {(selectedPage || entry) && (
            <div>
              <label style={labelStyle}>Channels</label>
              <label
                style={{
                  ...channelOptionStyle,
                  border: '1px solid var(--border-1)',
                  borderRadius: 'var(--radius-md)',
                  background: scopeMode === 'all' ? 'var(--accent-soft)' : 'var(--bg-surface-1)',
                }}
              >
                <input
                  type="checkbox"
                  checked={scopeMode === 'all'}
                  onChange={(e) => toggleAllCurrentChannels(e.target.checked)}
                  aria-label="All current channels"
                />
                <span>
                  <strong style={{ display: 'block', color: 'var(--fg-1)' }}>All current channels</strong>
                  <span style={{ display: 'block', marginTop: 2, color: 'var(--fg-3)', fontSize: 12 }}>
                    {rosterSize > 0
                      ? `Covers the ${rosterSize === 1 ? 'channel' : `${rosterSize} channels`} on this page today; channels added later aren't included.`
                      : "Covers the channels on this page today; channels added later aren't included."}
                  </span>
                </span>
              </label>

              <div style={{ marginTop: 10 }}>
                <input
                  style={fieldStyle}
                  value={channelSearch}
                  onChange={(e) => setChannelSearch(e.target.value)}
                  placeholder="Search channels by name or ID…"
                  aria-label="Search target channels"
                />
                <div style={channelPickerStyle}>
                  {visibleChannels.length === 0 ? (
                    <div style={{ ...hintStyle, margin: 0, padding: 10 }}>No channels match this search.</div>
                  ) : visibleChannels.map((channel) => {
                    const isCurrentChannel = channels.some((current) => current.channelId === channel.channelId);
                    const checked = scopeMode === 'all'
                      ? isCurrentChannel
                      : selectedChannelIds.includes(channel.channelId);
                    return (
                      <label key={channel.channelId} style={channelOptionStyle} title={channel.channelId}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleChannel(channel.channelId)}
                        />
                        <span style={{ minWidth: 0, fontWeight: 600, color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {channelLabelOf(channel)}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div style={hintStyle}>
                  {scopeMode === 'all'
                    ? `All ${channels.length} current channels selected`
                    : `${selectedCount} selected`}
                  {entry && !scopeDirty ? ` · saved scope: ${targetScopeLabel(entry)}` : ''}
                </div>
              </div>
            </div>
          )}
        </Section>

        <Section title="What">
          <div>
            <label style={labelStyle}>Moment</label>
            <select
              style={{ ...fieldStyle, cursor: 'pointer' }}
              value={momentMode === 'custom' ? '__custom__' : tentpoleSlug}
              aria-label="Moment"
              onChange={(e) => {
                if (e.target.value === '__custom__') {
                  setMomentMode('custom'); // keep tentpoleSlug for painless toggle-back
                } else {
                  setMomentMode('tentpole');
                  setTentpoleSlug(e.target.value);
                }
              }}
            >
              <option value="">Select a tentpole…</option>
              {upcomingTentpoles.length > 0 && <optgroup label="Upcoming">{upcomingTentpoles.map(tentpoleOption)}</optgroup>}
              {pastTentpoles.length > 0 && <optgroup label="Past">{pastTentpoles.map(tentpoleOption)}</optgroup>}
              <option value="__custom__">✦ Custom moment (not on the list)…</option>
            </select>
            {momentMode === 'custom' && (
              <>
                <input
                  style={{ ...fieldStyle, marginTop: 8 }}
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  maxLength={80}
                  placeholder="e.g. Creator's album drop, local festival…"
                  aria-label="Custom moment name"
                />
                <div style={hintStyle}>
                  A moment that isn't a tentpole. It skips tentpole windows and capacity; creators can hold up to
                  15 active custom plans. A recurring one can become a tentpole from the plan's detail.
                </div>
              </>
            )}
          </div>

          <div>
            <label style={labelStyle}>Title</label>
            <input
              style={fieldStyle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="e.g. Haunted mansion 24-hour livestream"
            />
          </div>

          <div>
            <label style={labelStyle}>Format</label>
            <FilterChipGroup options={CONTENT_TYPE_OPTIONS} value={contentType} onChange={setContentType} size="sm" />
          </div>

          <div>
            <label style={labelStyle}>Plan status</label>
            <select
              style={{ ...fieldStyle, cursor: 'pointer', maxWidth: 260 }}
              value={planStatus}
              aria-label="Plan status"
              onChange={(e) => setPlanStatus(e.target.value as CalendarPlanStatus)}
            >
              {CALENDAR_PLAN_STATUSES.map((value) => (
                <option key={value} value={value}>{PLAN_STATUS_LABELS[value]}</option>
              ))}
            </select>
            <div style={hintStyle}>How sure the creator is. Separate from CG review.</div>
          </div>
        </Section>

        <Section title="When">
          <div>
            <label style={labelStyle}>Precision</label>
            <FilterChipGroup options={PRECISION_OPTIONS} value={datePrecision} onChange={setPrecision} size="sm" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, alignItems: 'start' }}>
            <div>
              <label style={labelStyle}>{datePrecision === 'month' ? 'Planned month' : 'Planned date'}</label>
              {datePrecision === 'month' ? (
                <input
                  type="month"
                  style={fieldStyle}
                  value={plannedDate.slice(0, 7)}
                  aria-label="Planned month"
                  onChange={(e) => setPlannedDate(e.target.value ? `${e.target.value}-01` : '')}
                />
              ) : (
                <input
                  type="date"
                  style={fieldStyle}
                  value={plannedDate}
                  aria-label="Planned date"
                  onChange={(e) => setPlannedDate(e.target.value)}
                />
              )}
              {datePrecision === 'week' && <div style={hintStyle}>The plan covers the seven days from this date.</div>}
            </div>
            {showEndDate && (
              <div>
                <label style={labelStyle}>End date</label>
                <input
                  type="date"
                  style={fieldStyle}
                  value={plannedEndDate}
                  min={plannedDate || undefined}
                  onChange={(e) => setPlannedEndDate(e.target.value)}
                  aria-label="Planned end date (optional)"
                />
              </div>
            )}
          </div>
          {showEndDate ? (
            <button
              type="button"
              style={linkButtonStyle}
              onClick={() => { setShowEndDate(false); setPlannedEndDate(''); }}
            >
              Remove the end date
            </button>
          ) : (
            <button type="button" style={linkButtonStyle} onClick={() => setShowEndDate(true)}>
              + Add an end date
            </button>
          )}

          <div>
            <label style={labelStyle}>Recurrence</label>
            <select
              style={{ ...fieldStyle, cursor: 'pointer', maxWidth: 260 }}
              value={recurrence}
              aria-label="Recurrence"
              onChange={(e) => setRecurrence(e.target.value as CalendarRecurrence)}
            >
              {CALENDAR_RECURRENCES.map((value) => (
                <option key={value} value={value}>{RECURRENCE_LABELS[value]}</option>
              ))}
            </select>
          </div>
        </Section>

        <Section title="Notes">
          <div>
            <label style={labelStyle}>{CALENDAR_FIELD_LABELS.advertiserSummary}</label>
            <textarea
              style={{ ...fieldStyle, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
              value={advertiserSummary}
              onChange={(e) => setAdvertiserSummary(e.target.value)}
              maxLength={280}
              placeholder="Optional, spoiler-safe context that may be shared with advertisers."
            />
            <div style={{ ...hintStyle, textAlign: 'right' }}>{advertiserSummary.length}/280</div>
          </div>

          {entry && (
            <div>
              <label style={labelStyle}>{CALENDAR_FIELD_LABELS.creatorPrivateNote}</label>
              <div
                style={{
                  minHeight: 44,
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--warning)',
                  background: 'var(--warning-soft)',
                  color: entry.creatorPrivateNote ? 'var(--fg-1)' : 'var(--fg-3)',
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {entry.creatorPrivateNote || 'No private note was provided.'}
              </div>
              <div style={hintStyle}>
                Written by the creator and read-only here. Keep staff context in the Internal CG note below.
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>{CALENDAR_FIELD_LABELS.internalNote}</label>
            <textarea
              style={{ ...fieldStyle, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              maxLength={4000}
              placeholder="CG-authored context — never shown to creators or advertisers."
            />
          </div>
        </Section>
      </div>
    </Modal>
  );
};

export default EntryEditor;
